import { useState, useContext } from "react";
import { useApolloClient } from "@apollo/client";
import { baseQuery } from "../graphql/base.query";
import { ContextEDH } from "@context/ContextEDH";
import { Types } from "@context/base.reducer";
import { BaseQuery, ThemeEnum } from "@models/ISchema";
import { ThemeMode, ThemeStore } from "@foris/avocado-suite";
import { useHistory } from "react-router-dom";
import { getCacheRangesByScenario } from "@config/CalendarRanges/calendarRanges";
import { useTranslation } from "react-i18next";
import { getDynamicHomeUrl } from "@common/hooks/useNavigationUrl";

type Response = { data: BaseQuery; error: boolean; loading: boolean };

const useGetDataBase = (): [Response, (id: string, scenarioId?: string) => void] => {
  const history = useHistory();
  const client = useApolloClient();
  const { dispatch, state } = useContext(ContextEDH);
  const [response, setResponse] = useState<Response>(null);

  const {
    i18n: { changeLanguage },
  } = useTranslation();

  const isSavedContextLoaded = !!window?.localStorage?.getItem("savedContext");

  const getDataBase = async (id: string, scenarioId?: string) => {
    setResponse({ data: null, error: false, loading: true });
    try {
      const { data } = await client.query({
        query: baseQuery,
        variables: {
          id: parseInt(id),
        },
      });
      const base: BaseQuery = data?.base;
      const currentUser = base.user;

      dispatch({ type: Types.SetBase, payload: base });
      const res = { data: base, error: false, loading: false };
      setResponse(res);

      if (currentUser) {
        const locale = currentUser?.preferences?.locale ?? "es";
        // The preference can come back spelled either way (`es_MX` or `es-MX`), and only
        // the language part names a locale the app ships translations for.
        const parsedLocale = locale?.split(/[-_]/)[0] as "es" | "en";

        changeLanguage(parsedLocale);
        dispatch({
          type: Types.setLocale,
          payload: parsedLocale,
        });

        ThemeStore.setThemeMode(
          (currentUser?.preferences?.theme ?? ThemeEnum.Light) as ThemeMode,
          true,
        );
      }

      if (scenarioId || currentUser?.preferences?.cubeContext?.scenarioId) {
        const newRanges = await getCacheRangesByScenario(
          scenarioId || currentUser?.preferences?.cubeContext?.scenarioId,
          client,
        );

        if (newRanges) {
          localStorage.setItem("range-start-time", newRanges.start);
          localStorage.setItem("range-end-time", newRanges.end);
        }
      }

      if (!state?.base?.base && currentUser?.preferences?.cubeContext && !isSavedContextLoaded) {
        const { workspaceId, scenarioId, originId } =
          currentUser?.preferences?.cubeContext ?? currentUser?.context ?? {};

        history.replace(getDynamicHomeUrl(workspaceId, scenarioId, originId));

        window.localStorage.setItem("savedContext", "true");
      }

      return [res, getDataBase];
    } catch (error) {
      setResponse({ data: null, error: true, loading: false });
      dispatch({ type: Types.SetBase, payload: null });
      console.error("HOOK-getDataBase", error);
    }
  };

  return [response, getDataBase];
};

export default useGetDataBase;
